import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IFavouriteItem } from './favourite.service';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { isPlatformServer } from '@angular/common';

export interface IRegMessage {
  status: 'ok'|'error';
  message: string;
}

export interface ICoords {
  latitude: number,
  longitude: number
}

export interface ILocation {
  id: number,
  cityNameI: string,
  latitude: number,
  longitude: number,
  oktmoCode: number
}

export interface IAuthMessage extends IRegMessage {
  code?: string;
}

export interface IUserInfo {
  username?: string;
  status?: 'active'|'partial_registration'|'archived'|'agent_confirmation'|'agent_active'|'agent_draft';
  userType?: 'client'|'agent'|'partner';
  isSelfEmployee?: boolean;
  agreementStatus?: false;
  guid?: string;
  isIndividual: boolean;
  phone?: string;
  timezone?: string;
  permissions?: string[];//todo::check or add interface for permissions
  favourites?: IFavouriteItem[];
  organisationId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  GEO_COOKIE_TITLE = 'ndvGeo';
  private timezone$= new BehaviorSubject('+00:00');
  private userGuid$ = new BehaviorSubject((null as string | null));
  private _status$ = new BehaviorSubject((null as string | null))
  private _username$ = new BehaviorSubject((null as string | null));
  private phone$ = new BehaviorSubject('');
  private _favourites$ = new BehaviorSubject((null as IFavouriteItem[] | null));
  private _organisationId$ = new BehaviorSubject((null as number | null))
  private unsubscribe$ = new Subject();
  private _ndvUser$ = new BehaviorSubject((null as IUserInfo | null));
  private _location$ = new BehaviorSubject((null as ILocation | null))
  redirectUrl: string | null = null;
  user: IUserInfo;

  constructor(
    private http: HttpClient,
    private cookieS: CookieService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.user = {
      isIndividual: false,
    };
  }

  getUserFromServer(): void {
    const url = environment.apiBaseUrl + '/cabinet/security/user-info';
    this.http.post<any>(url, null).pipe(takeUntil(this.unsubscribe$)).subscribe((data: IUserInfo) => {
      this.updateUserInfo(data);
    });
  }

  getUser(): Observable<any> {
    const url = environment.apiBaseUrl + '/cabinet/security/user-info';
    return this.http.post<any>(url, null);
  }

  updateUserInfo(data: IUserInfo) {
    this._ndvUser$.next(data);
    if (data.hasOwnProperty('username') && data['username']) {
      this.user.username = (data.username as string);
      this._username$.next((data.username as string));
    }
    if (data.hasOwnProperty('timezone') && data['timezone']) {
      this.timezone$.next(data.timezone ?? '+00:00');
    }
    if (data.hasOwnProperty('guid') && data.guid) {
      this.user.guid = data.guid as string;
      this.userGuid$.next((data.guid as string));
    }
    if (data.hasOwnProperty('phone') && data['phone']) {
      if (data.phone != null) {
        this.user.phone = data.phone;
        this.phone$.next(data.phone);
      }
    }
    if (data.hasOwnProperty('favourites')) {
      this._favourites$.next(data.favourites as IFavouriteItem[]);
    }

    if (data.hasOwnProperty('organisationId')) {
      this.user.organisationId = data.organisationId;
      this._organisationId$.next(data.organisationId as number);
    }

    if (data.hasOwnProperty('agreementStatus')) {
      this.user.agreementStatus = data.agreementStatus;
    }
  }

  get location$(): BehaviorSubject<ILocation | null> {
    return this._location$;
  }

  get status$(): BehaviorSubject<string | null> {
    return this._status$;
  }
  get username$(): BehaviorSubject<string | null> {
    return this._username$;
  }

  get favourites$(): BehaviorSubject<IFavouriteItem[] | null> {
    return this._favourites$;
  }

  set favourites$(value: BehaviorSubject<IFavouriteItem[] | null>) {
    this._favourites$ = value;
  }

  get organisationId$() {
    return this._organisationId$;
  }

  get ndvUser$() {
    return this._ndvUser$;
  }

  get isLoggedIn(): boolean {
    return this.cookieS.get('accessToken') !== '';
  }

  register(phone: string): Observable<any> {
    return this.http.post(environment.apiBaseUrl + '/get-verification-code', { phone });
  }

  login(phone: string, code: string): Observable<string | null> {
    return this.http.post(environment.apiBaseUrl + '/authorize-by-phone-code', { phone, code }).pipe(
      takeUntil(this.unsubscribe$),
      switchMap((res: any) => {
        if (res['code'] !== undefined) {
          this.cookieS.set('accessToken', res['code'], { expires: new Date(new Date().getTime() +  1000 * 60 * 60) });
          return of(res['code']);
        }
        return of(null);
      }),
    );
  }

  getGeoLocation() {
    const geoCookieString = this.cookieS.get(this.GEO_COOKIE_TITLE);

    if (geoCookieString === '') {
      this.handleGeoPermission();
    } else {
      const geoCookie = JSON.parse(this.cookieS.get(this.GEO_COOKIE_TITLE));
      this._location$.next(geoCookie)
    }
  }


  handleGeoPermission(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // Запрос на бэк, сохранить локацию
    if (navigator.geolocation) {
      navigator.permissions.query({name:'geolocation'}).then((result) => {
        if (result.state === 'granted') {
          this.getLocationByCoords();
        } else if (result.state === 'prompt') {
          console.log(result.state);
          // if prompt success
          this.getLocationByCoords();
        } else if (result.state === 'denied') {
          console.log(result.state);
        }
        result.onchange = function() {
          console.log(result.state);
        }
      });
    }
  }

  getLocationByCoords(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    navigator.geolocation.getCurrentPosition((p) => {
      // Запрос к апи с координатами для определения ближайшего нас пункта, возможно, города из предложений
      const url = environment.apiBaseUrl + '/realty/page-params/city-by-coords';

      this.http.post(url, <ICoords>{
        latitude: p.coords.latitude,
        longitude: p.coords.longitude
      }).pipe(takeUntil(this.unsubscribe$)).subscribe((res) => {
        this._location$.next(res as ILocation);
        this.cookieS.set(this.GEO_COOKIE_TITLE, JSON.stringify(res));
      });
    });
  }
}
